import { useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import type { SiteTheme } from "@circle-react-shared/BuilderUI/SiteThemes/constants";
import { DEFAULT_THEMES_QUERY_KEY } from "@circle-react-shared/BuilderUI/SiteThemes/useGetThemes";
import { useUpdateSiteTheme } from "@circle-react-shared/BuilderUI/SiteThemes/useUpdateSiteTheme";
import { Button } from "@circle-react-shared/uikit/Button";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circle-react-uikit/ModalV2";
import { RENAME_THEME_FORM_VALIDATION_SCHEMA } from "./constants";

interface RenameCustomThemeModalProps {
  currentCanvasTheme: SiteTheme;
  toggleCurrentCanvasTheme: (theme: SiteTheme) => void;
}

interface FormData {
  theme_name: string;
}

const RenameCustomThemeModalComponent = ({
  currentCanvasTheme,
  toggleCurrentCanvasTheme,
}: RenameCustomThemeModalProps) => {
  const modal = useModal();
  const queryClient = useQueryClient();

  const { mutate: updateTheme, isLoading: isUpdatingTheme } =
    useUpdateSiteTheme();

  const handleSubmit = (formData: FormData) => {
    updateTheme(
      {
        id: currentCanvasTheme.value,
        theme_name: formData.theme_name,
        set_as_default_theme: false,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries(DEFAULT_THEMES_QUERY_KEY);
          toggleCurrentCanvasTheme({
            ...currentCanvasTheme,
            name: formData.theme_name,
          });
          void modal.hide();
        },
      },
    );
  };

  return (
    <Modal
      isOpen={modal.visible}
      onClose={() => {
        if (isUpdatingTheme) return;

        void modal.hide();
      }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Form
          validationSchema={RENAME_THEME_FORM_VALIDATION_SCHEMA}
          defaultValues={{ theme_name: currentCanvasTheme.name }}
          onSubmit={handleSubmit}
        >
          <Modal.Header>
            <Modal.Title size="sm" className="font-bold">
              {t(
                "builder_ui.theme_configuration.rename_custom_theme_modal.title",
              )}
            </Modal.Title>
            <Modal.HeaderActions>
              <Modal.CloseButton
                onClick={modal.hide}
                disabled={isUpdatingTheme}
              />
            </Modal.HeaderActions>
          </Modal.Header>
          <Modal.Body className="overflow-y-auto pb-11 md:!px-8">
            <Typography.BodyMd color="text-dark">
              {t(
                "builder_ui.theme_configuration.rename_custom_theme_modal.description",
              )}
            </Typography.BodyMd>
            <Form.Item
              name="theme_name"
              label={t(
                "builder_ui.theme_configuration.rename_custom_theme_modal.form.name.label",
              )}
              hideDescription
              hideBorder
              placeholder={t(
                "builder_ui.theme_configuration.rename_custom_theme_modal.form.name.placeholder",
              )}
            >
              <Form.Input name="theme_name" />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="border-primary flex w-full justify-end gap-4 border-t">
            <Button
              variant="secondary"
              onClick={modal.hide}
              type="button"
              disabled={isUpdatingTheme}
            >
              {t(
                "builder_ui.theme_configuration.rename_custom_theme_modal.secondary_cta",
              )}
            </Button>
            <Button variant="circle" type="submit" disabled={isUpdatingTheme}>
              {t(
                "builder_ui.theme_configuration.rename_custom_theme_modal.primary_cta",
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

const RenameCustomThemeModal = ModalManager.create(
  ({
    currentCanvasTheme,
    toggleCurrentCanvasTheme,
  }: RenameCustomThemeModalProps) => (
    <RenameCustomThemeModalComponent
      currentCanvasTheme={currentCanvasTheme}
      toggleCurrentCanvasTheme={toggleCurrentCanvasTheme}
    />
  ),
);

export const useRenameCustomThemeModal = () => useModal(RenameCustomThemeModal);
